import { useState, useEffect, useRef, useCallback } from "react";
import { ReceiptModal } from "./ReceiptModal";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileText, ChevronRight, ChevronLeft, Printer } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { printReceiptSilent, isQzTrayAvailable, getDefaultPrinter } from "@/utils/qzPrint";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * ThermalVatInvoiceModal — Renders and auto-prints a thermal-friendly (80mm)
 * VAT invoice at the till, using the same style as the normal receipt.
 */
const ThermalVatInvoiceModal = ({ open, onClose, transaction, copies = 1 }) => {
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  const [vatInvoice, setVatInvoice] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPrinted, setHasPrinted] = useState(false);
  const [qzAvailable, setQzAvailable] = useState(false);
  const printRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // Reset state on open
  useEffect(() => {
    if (!open) {
      setVatInvoice(null);
      setLoading(true);
      setHasPrinted(false);
    }
  }, [open]);

  // Check QZ Tray
  useEffect(() => {
    isQzTrayAvailable().then(setQzAvailable).catch(() => setQzAvailable(false));
  }, []);

  // Fetch VAT invoice data — use vat_company from the invoice for the header
  useEffect(() => {
    if (!open || !transaction?.auto_vat_invoice_id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const vatRes = await axios.get(
          `${API}/documents/vat_invoices/${transaction.auto_vat_invoice_id}/data`,
          { headers }
        ).catch(() => null);

        if (vatRes?.data) {
          setVatInvoice(vatRes.data);
          // Use the vat_company snapshot stored on the invoice (VITEX FARMING)
          const vc = vatRes.data.vat_company || {};
          setCompanyInfo({
            name: vc.company_name || "VITEX FARMING",
            address: vc.address_line1 || "",
            city: vc.city || "",
            phone: vc.phone || "",
            email: vc.email || "",
            vatNumber: vc.vat_number ? `VAT: ${vc.vat_number}` : "",
            logo: vc.logo_url || vc.receipt_logo || "",
          });
        }
      } catch (err) {
        console.error("Failed to fetch VAT invoice data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, transaction]);

  // Auto-print once loaded
  useEffect(() => {
    if (!open || loading || !vatInvoice || hasPrinted || !printRef.current) return;

    const printTimer = setTimeout(async () => {
      const receiptHtml = printRef.current?.innerHTML;
      if (!receiptHtml) return;

      // Try QZ Tray for silent printing
      if (qzAvailable && getDefaultPrinter()) {
        try {
          const result = await printReceiptSilent(receiptHtml, copies);
          if (result.success) {
            toast.success(`VAT Invoice printed to ${result.printer}`);
            setHasPrinted(true);
            setTimeout(() => onCloseRef.current(), 300);
            return;
          }
        } catch (e) {
          console.warn("QZ Tray failed, fallback to browser:", e);
        }
      }

      // Browser print fallback.
      // ------------------------------------------------------------
      // See ReceiptModal / qzPrint.js for the full history — the
      // WHOLE VAT invoice must render as ONE continuous auto-height
      // page. Any `page-break-inside: avoid` on totals/payment blocks
      // (which was here previously) forces Chromium to shove the
      // block onto a fresh page-box the moment the item list is
      // long enough — and the auto-cutter fires between them. That's
      // the "cuts after line 6/7" symptom.
      const thermalStyles = `
        <style>
          @page { size: 72mm auto; margin: 0mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          html, body { break-inside: auto !important; page-break-inside: auto !important; }
          body { font-family: 'Courier New', 'Lucida Console', monospace; font-size: 14px; font-weight: 900 !important; line-height: 1.4; width: 70mm; margin: 0 auto; padding: 1mm; color: #000 !important; text-transform: uppercase; -webkit-font-smoothing: none; }
          .receipt { width: 100%; font-weight: 900 !important; }
          .copy-separator { page-break-after: always; margin: 8mm 0; padding-top: 4mm; }
          .copy-label { text-align: center; font-weight: 900 !important; font-size: 14px; margin-bottom: 2mm; }
          .text-center { text-align: center; }
          div, span, p { font-weight: 900 !important; color: #000 !important; }
          /* Force EVERY block to allow natural flow — no page-break-avoid
             hints. This is what stops the "cuts between items and totals"
             bug once and for all. */
          .receipt, .receipt *, .totals-section, .payment-info, .total-line {
            page-break-inside: auto !important;
            break-inside: auto !important;
            page-break-before: auto !important;
            page-break-after: auto !important;
            break-before: auto !important;
            break-after: auto !important;
          }
          img { width: 100% !important; max-width: 68mm !important; min-height: 20mm; height: auto !important; display: block !important; margin: 0 auto 6mm auto !important; }
          /* Trailing feed strip — 45 mm covers the worst-case thermal
             auto-cutter offset so the blade never bites the last line. */
          .receipt-feed-zone { height: 45mm; line-height: 45mm; }
          @media print {
            * { -webkit-print-color-adjust: exact !important; }
            html, body { width: 72mm; margin: 0; padding: 1mm; }
            .receipt, .receipt *, .totals-section, .payment-info, .total-line {
              page-break-inside: auto !important;
              break-inside: auto !important;
              page-break-before: auto !important;
              page-break-after: auto !important;
              break-before: auto !important;
              break-after: auto !important;
            }
            .receipt-feed-zone { height: 45mm; }
          }
        </style>
      `;

      const printWindow = window.open("", "_blank", "width=302,height=600");
      if (!printWindow) {
        toast.error("Pop-up blocked. Please allow pop-ups for printing.");
        return;
      }

      printWindow.document.write(`
        <!DOCTYPE html><html><head><title>VAT Invoice</title>${thermalStyles}</head><body>
          ${copies === 2 ? '<div class="copy-label">--- COPY 1 (CUSTOMER) ---</div>' : ''}
          ${receiptHtml}
          <div class="receipt-feed-zone">&nbsp;</div>
          ${copies === 2 ? `
            <div class="copy-separator"></div>
            <div class="copy-label">--- COPY 2 (YARD/STORE) ---</div>
            ${receiptHtml}
            <div class="receipt-feed-zone">&nbsp;</div>
          ` : ''}
        </body></html>
      `);
      printWindow.document.close();
      printWindow.focus();

      const images = printWindow.document.querySelectorAll("img");
      let loadedCount = 0;
      const tryPrint = () => {
        printWindow.print();
        printWindow.close();
        setHasPrinted(true);
        setTimeout(() => onCloseRef.current(), 300);
      };

      if (images.length === 0) {
        setTimeout(tryPrint, 300);
      } else {
        images.forEach((img) => {
          const onDone = () => { loadedCount++; if (loadedCount === images.length) setTimeout(tryPrint, 300); };
          if (img.complete) onDone(); else { img.onload = onDone; img.onerror = onDone; }
        });
        setTimeout(() => { if (loadedCount < images.length) tryPrint(); }, 2000);
      }
    }, 600);

    return () => clearTimeout(printTimer);
  }, [open, loading, vatInvoice, hasPrinted, qzAvailable, copies]);

  const handleManualPrint = () => {
    if (!printRef.current) return;
    const receiptHtml = printRef.current.innerHTML;
    const printWindow = window.open("", "_blank", "width=302,height=600");
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>VAT Invoice</title>
      <style>
        @page { size: 72mm auto; margin: 0mm; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Courier New', monospace; font-size: 14px; font-weight: 900; line-height: 1.4; width: 70mm; margin: 0 auto; padding: 1mm; color: #000; text-transform: uppercase; }
        .text-center { text-align: center; }
        div, span, p { font-weight: 900; color: #000; }
        img { width: 100%; max-width: 68mm; display: block; margin: 0 auto 6mm auto; }
      </style>
      </head><body>${receiptHtml}</body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-ZA", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  // Use vatInvoice data or fall back to transaction data
  const inv = vatInvoice || {};
  const items = inv.items || transaction?.items || [];
  const total = inv.total || transaction?.total || 0;
  // Force 15% VAT calculation from the total — never rely on global vatEnabled
  const subtotal = inv.subtotal || round2(total / 1.15);
  const vat = inv.tax || round2(total - total / 1.15);
  const vatNumber = inv.vat_company?.vat_number
    ? `VAT: ${inv.vat_company.vat_number}`
    : companyInfo?.vatNumber || "";
  const customerName = inv.customer_name || transaction?.customer_name || "";
  const customerAddress = inv.customer_address || transaction?.customer_address || "";
  const customerSuburb = inv.customer_suburb || transaction?.customer_suburb || "";
  const customerProvince = inv.customer_province || transaction?.customer_province || "";
  const customerVatNumber = inv.customer_vat_number || transaction?.customer_vat_number || "";
  const invoiceNumber = inv.vat_invoice_number || transaction?.auto_vat_invoice_number || "";
  const invoiceDate = inv.created_at || transaction?.created_at || "";

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            VAT Invoice (Thermal)
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center h-40 text-gray-500">Loading VAT invoice...</div>
        )}

        <div className="flex justify-end gap-2 mb-2">
          <Button variant="outline" onClick={handleManualPrint}>
            <Printer className="h-4 w-4 mr-2" />
            Reprint
          </Button>
          <Button onClick={onClose}>Done</Button>
        </div>

        {/* Thermal receipt preview */}
        <div className="bg-white p-2 border rounded max-h-[60vh] overflow-y-auto">
          <div ref={printRef}>
            <div className="receipt font-mono uppercase" style={{ width: "70mm", fontWeight: "bold", color: "#000" }}>
              {/* Logo */}
              {companyInfo?.logo && (
                <div className="text-center" style={{ marginBottom: "6mm" }}>
                  <img src={companyInfo.logo} alt="" style={{ width: "100%", maxWidth: "68mm", height: "auto", minHeight: "20mm", objectFit: "contain", display: "block", margin: "0 auto" }} />
                </div>
              )}

              {/* Company Header */}
              <div className="text-center" style={{ marginBottom: "5mm", fontSize: "16px", fontWeight: "bold" }}>
                <div style={{ fontSize: "18px", fontWeight: "900" }}>{companyInfo?.name || "TAX INVOICE"}</div>
                {companyInfo?.address && <div>{companyInfo.address}</div>}
                {companyInfo?.city && <div>{companyInfo.city}</div>}
                {companyInfo?.phone && <div>TEL: {companyInfo.phone}</div>}
                {vatNumber && <div>{vatNumber}</div>}
              </div>

              <div style={{ height: "3mm" }} />

              {/* TAX INVOICE Header */}
              <div className="text-center" style={{ fontSize: "18px", fontWeight: "900", marginBottom: "3mm", padding: "2mm", border: "2px solid black" }}>
                TAX INVOICE
              </div>
              <div className="text-center" style={{ fontSize: "16px", fontWeight: "900", marginBottom: "3mm" }}>
                {invoiceNumber}
              </div>

              <div style={{ height: "3mm" }} />

              {/* Date & Salesperson */}
              <div style={{ fontSize: "14px", fontWeight: "bold" }}>
                <div>Date: {formatDate(invoiceDate)}</div>
                <div>Sales Person: {transaction?.cashier || "Cashier 1"}</div>
                {transaction?.transaction_number && (
                  <div>Ref: {transaction.transaction_number}</div>
                )}
              </div>

              {/* Customer Details */}
              {customerName && (
                <>
                  <div style={{ height: "4mm" }} />
                  <div style={{ fontSize: "14px", fontWeight: "bold" }}>
                    <div style={{ fontWeight: "900" }}>CUSTOMER:</div>
                    <div>{customerName}</div>
                    {transaction?.customer_account_number && <div>A/C: {transaction.customer_account_number}</div>}
                    {customerAddress && <div>{customerAddress}</div>}
                    {customerSuburb && <div>{customerSuburb}</div>}
                    {customerProvince && <div>{customerProvince}</div>}
                    {customerVatNumber && <div>VAT NO: {customerVatNumber}</div>}
                  </div>
                </>
              )}

              <div style={{ height: "5mm" }} />

              {/* Line Items with VAT breakdown */}
              <div style={{ fontSize: "14px", fontWeight: "bold" }}>
                {items.map((item, i) => {
                  const lineTotal = item.total || (item.quantity || 1) * (item.unit_price || item.price || 0);
                  const lineExcl = lineTotal / 1.15;
                  const lineVat = lineTotal - lineExcl;
                  return (
                    <div key={i} style={{ marginBottom: "3mm" }}>
                      <div style={{ fontWeight: "900" }}>{item.name || item.description}</div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>{item.quantity} x {(item.unit_price || item.price || 0).toFixed(2)}</span>
                        <span>{lineTotal.toFixed(2)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                        <span>EXCL: {lineExcl.toFixed(2)}</span>
                        <span>VAT: {lineVat.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ height: "3mm" }} />
              <div style={{ borderTop: "2px dashed black", margin: "2mm 0" }} />

              {/* Totals — no page-break hints. See ReceiptModal / qzPrint.js
                  for the full "auto flow everywhere" rationale. */}
              <div className="totals-section" style={{ pageBreakInside: "auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: "bold" }}>
                  <span>SUBTOTAL (EXCL):</span>
                  <span>R{subtotal.toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: "bold" }}>
                  <span>VAT (15%):</span>
                  <span>R{vat.toFixed(2)}</span>
                </div>
                <div style={{ height: "2mm" }} />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "20px", fontWeight: "900" }}>
                  <span>TOTAL (INCL):</span>
                  <span>R{total.toFixed(2)}</span>
                </div>

                <div style={{ height: "4mm" }} />

                {/* Payment Info */}
                <div style={{ fontSize: "16px", fontWeight: "bold" }}>
                  {transaction?.payments?.map((p, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{p.method}:</span>
                      <span>{p.amount?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div style={{ height: "5mm" }} />

                {/* Footer */}
                <div className="text-center" style={{ fontSize: "12px", fontWeight: "bold" }}>
                  <div>--- TAX INVOICE ---</div>
                  <div>E&OE</div>
                </div>

                <div style={{ height: "15mm" }} />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * SplitInvoiceWrapper - Handles automatic invoice splitting for customers with split_groceries_invoice enabled
 * 
 * When a customer has split_groceries_invoice: true, this component:
 * 1. Splits items into GROCERIES vs everything else
 * 2. Creates two transactions with linked invoice numbers (e.g., INV-001A, INV-001B)
 * 3. Shows a navigation to switch between the two invoices
 */
export const SplitInvoiceWrapper = ({ 
  open, 
  onClose, 
  transaction, 
  vatEnabled = false, 
  autoPrint = false,
  copies = 1
}) => {
  const [splitTransactions, setSplitTransactions] = useState(null);
  const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState(0);
  const [showSplitDialog, setShowSplitDialog] = useState(false);

  useEffect(() => {
    // VITEX-branded split invoices have been retired. Regardless of any
    // legacy customer flag or transaction snapshot, we ALWAYS render only
    // the primary Bosveld invoice from here on. This effect is kept so the
    // component still resets state on close/open.
    setSplitTransactions(null);
    setCurrentInvoiceIndex(0);
    setShowSplitDialog(false);
  }, [open, transaction]);

  // If no split needed, always fall through to the normal Bosveld receipt.
  // Legacy `auto_vat_invoice_id` on some sales pointed to a VITEX-branded
  // VAT invoice — never render that to the customer again.
  if (!splitTransactions) {
    return (
      <ReceiptModal
        open={open}
        onClose={onClose}
        transaction={transaction}
        vatEnabled={vatEnabled}
        autoPrint={autoPrint}
        copies={copies}
      />
    );
  }

  const currentTransaction = splitTransactions[currentInvoiceIndex];
  const totalInvoices = splitTransactions.length;

  // Show split invoice selector first
  if (showSplitDialog) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              Split Invoice Created
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              This customer has <strong>Split Groceries Invoice</strong> enabled. 
              The purchase has been split into two invoices:
            </p>
            
            <div className="space-y-2">
              {splitTransactions.map((tx, idx) => (
                <div 
                  key={idx}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    idx === 0 ? 'border-green-300 bg-green-50 hover:bg-green-100' : 'border-blue-300 bg-blue-50 hover:bg-blue-100'
                  }`}
                  onClick={() => {
                    setCurrentInvoiceIndex(idx);
                    setShowSplitDialog(false);
                  }}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-bold">{tx.invoice_number}</span>
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                        idx === 0 ? 'bg-green-200 text-green-800' : 'bg-blue-200 text-blue-800'
                      }`}>
                        {tx.split_invoice_label}
                      </span>
                    </div>
                    <span className="font-bold">R {tx.total?.toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {tx.items.length} item{tx.items.length !== 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-2">
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => {
                  setCurrentInvoiceIndex(0);
                  setShowSplitDialog(false);
                }}
              >
                Print Groceries First
              </Button>
              <Button 
                className="flex-1"
                variant="outline"
                onClick={() => {
                  setCurrentInvoiceIndex(1);
                  setShowSplitDialog(false);
                }}
              >
                Print Other Items First
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show actual receipt with navigation
  return (
    <>
      <ReceiptModal
        open={open}
        onClose={onClose}
        transaction={currentTransaction}
        vatEnabled={vatEnabled}
        autoPrint={false} // Don't auto-print split invoices
        copies={copies}
      />
      
      {/* Invoice Navigation Overlay */}
      {open && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-[100] bg-white shadow-lg rounded-full px-4 py-2 flex items-center gap-3 border">
          <Button
            size="sm"
            variant="ghost"
            disabled={currentInvoiceIndex === 0}
            onClick={() => setCurrentInvoiceIndex(prev => prev - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <div className="text-sm font-medium">
            <span className={currentTransaction.split_invoice_part === 'A' ? 'text-green-600' : 'text-blue-600'}>
              {currentTransaction.invoice_number}
            </span>
            <span className="text-gray-400 mx-2">|</span>
            <span className="text-gray-600">
              Invoice {currentInvoiceIndex + 1} of {totalInvoices}
            </span>
          </div>
          
          <Button
            size="sm"
            variant="ghost"
            disabled={currentInvoiceIndex === totalInvoices - 1}
            onClick={() => setCurrentInvoiceIndex(prev => prev + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowSplitDialog(true)}
          >
            View All
          </Button>
        </div>
      )}
    </>
  );
};

export default SplitInvoiceWrapper;
